import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FinanceService } from '../finance.service';
import { SnackBarService } from '../../services/snackbar.service';
import { ConstantsService } from '../../services/constants-service';
import { ObservableRequestService } from '../../services/observable-request.service';
import { MdDialog, MdDialogConfig } from '@angular/material';
import { Headers } from '@angular/http';
import { BankStatementPreviewComponent } from '../../entities/transaction-monitoring/bank-statement-preview/bank-statement-preview.component';
import { ReanalyzeComparisonComponent } from '../../entities/finance/reanalyze-comparison/reanalyze-comparison.component';
import { ViewTransactionComponent } from '../../entities/finance/view-transaction/view-transaction.component';
import { CreateAccountDialogComponent } from './create-account-dialog/create-account-dialog.component';
import { FindAccountDialogComponent } from './find-account-dialog/find-account-dialog.component';
import { ConfirmDialogComponent } from '../../entities/finance/confirm-dialog/confirm-dialog.component';
import {
  TimelineMonthDetailsDialogComponent,
  TimelineMonthPeriodRow
} from './timeline-month-details-dialog/timeline-month-details-dialog.component';
import { DuplicatesInvestigationDialogComponent } from './duplicates-investigation-dialog/duplicates-investigation-dialog.component';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

interface BankStatementBatchItem {
  id: number;
  fileName: string;
  uniqueNameId: string;
  processingState: string;
  confirmed: boolean;
  pushedToTransactionMonitoring?: boolean;
  transactionRecordCount?: number;
  extractedAccountOwner?: string;
  extractedAccountNumber?: string;
  extractedCurrency?: string;
  processingStartDate?: number; // Timestamp in milliseconds
  firstTxDate?: number; // Timestamp in milliseconds
  lastTxDate?: number; // Timestamp in milliseconds
  txCount?: number;
  statementStartDate?: number; // Timestamp in milliseconds
  statementEndDate?: number; // Timestamp in milliseconds
  bankAccount?: {
    accountId: number;
    accountName: string;
    accountNumber: string;
    currencyValue?: string;
  };
  client?: {
    clientId: number;
    clientName: string;
  };
  matchedClientId?: number;
  matchedBankAccountId?: number;
  jsonData?: string;
  transactionCount?: number;
  // Computed properties for sorting
  clientName?: string;
  bankAccountName?: string;
}

@Component({
  selector: 'app-bank-statements-batch',
  templateUrl: './bank-statements-batch.component.html',
  styleUrls: ['./bank-statements-batch.component.css']
})
export class BankStatementsBatchComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('dropZone') dropZone: ElementRef;

  bankStatements: BankStatementBatchItem[] = [];
  filteredStatements: BankStatementBatchItem[] = [];
  showAll = false;
  clientSearch: string = '';
  bankAccountSearch: string = '';
  totalRecords = 0;
  currentPage = 1;
  pageSize = 20;
  sortField: string = null;
  sortOrder: number = 1; // 1 for ascending, -1 for descending
  isDragging = false;
  isUploading = false;
  dragOver = false;
  retryingFailed = false;
  backfillingAccountOwner = false;
  backfillingStatementDates = false;
  findingAccounts = false;
  pollingIntervals: Map<number, any> = new Map();
  reanalysisIntervals: Set<any> = new Set(); // Track reanalysis polling intervals
  showTimelineView = false;
  loadingTimeline = false;
  timelineData: any[] = [];

  constructor(
    private financeService: FinanceService,
    private snackBarService: SnackBarService,
    private requestService: ObservableRequestService,
    private dialog: MdDialog
  ) {}

  ngOnInit() {
    this.loadBankStatements();
  }

  ngOnDestroy() {
    // Clear all polling intervals
    this.pollingIntervals.forEach(interval => clearInterval(interval));
    this.pollingIntervals.clear();
    // Clear all reanalysis intervals
    this.reanalysisIntervals.forEach(interval => clearInterval(interval));
    this.reanalysisIntervals.clear();
  }

  retryFailedStatements() {
    if (this.retryingFailed) {
      return;
    }

    this.retryingFailed = true;
    const url = `/bank-statement/batch/retry-failed`;
    
    this.requestService.postRequest(url, {}, new Headers()).subscribe(
      (response: any) => {
        this.retryingFailed = false;
        if (response && response.count !== undefined) {
          const count = response.count || 0;
          const totalFailed = response.totalFailed || 0;
          if (count > 0) {
            this.snackBarService.showSimpleMsj(`Queued ${count} failed bank statement(s) for retry. They will be processed sequentially.`);
            // Reload the list to show updated processing states
            this.loadBankStatements();
          } else {
            this.snackBarService.showSimpleMsj('No failed bank statements found to retry.');
          }
        } else {
          this.snackBarService.showSimpleMsj('Retry request completed. Please refresh to see updated status.');
          this.loadBankStatements();
        }
      },
      (error) => {
        this.retryingFailed = false;
        console.error('Error retrying failed bank statements:', error);
        this.snackBarService.showSimpleMsj('Failed to retry bank statements: ' + (error.message || 'Unknown error'));
      }
    );
  }

  backfillExtractedAccountOwner() {
    if (this.backfillingAccountOwner) {
      return;
    }

    this.backfillingAccountOwner = true;
    const url = ConstantsService.BANK_STATEMENT_BATCH_BACKFILL_ACCOUNT_OWNER;
    
    this.requestService.postRequest(url, {}, new Headers()).subscribe(
      (response: any) => {
        this.backfillingAccountOwner = false;
        if (response && response.count !== undefined) {
          const count = response.count || 0;
          if (count > 0) {
            this.snackBarService.showSimpleMsj(`Successfully backfilled extracted_account_owner for ${count} bank statement(s).`);
            // Reload the list to show updated account owners
            this.loadBankStatements();
          } else {
            this.snackBarService.showSimpleMsj('No bank statements found that need account owner backfill.');
          }
        } else {
          this.snackBarService.showSimpleMsj('Backfill completed. Please refresh to see updated status.');
          this.loadBankStatements();
        }
      },
      (error) => {
        this.backfillingAccountOwner = false;
        console.error('Error backfilling extracted account owner:', error);
        this.snackBarService.showSimpleMsj('Failed to backfill extracted account owner: ' + (error.message || 'Unknown error'));
      }
    );
  }

  backfillStatementDates() {
    if (this.backfillingStatementDates) {
      return;
    }

    this.backfillingStatementDates = true;
    const url = ConstantsService.BANK_STATEMENT_BATCH_BACKFILL_STATEMENT_DATES;
    
    this.requestService.postRequest(url, {}, new Headers()).subscribe(
      (response: any) => {
        this.backfillingStatementDates = false;
        if (response && response.count !== undefined) {
          const count = response.count || 0;
          if (count > 0) {
            this.snackBarService.showSimpleMsj(`Successfully backfilled statement dates for ${count} bank statement(s).`);
            // Reload the list to show updated dates
            this.loadBankStatements();
          } else {
            this.snackBarService.showSimpleMsj('No bank statements found that need statement dates backfill.');
          }
        } else {
          this.snackBarService.showSimpleMsj('Backfill completed. Please refresh to see updated status.');
          this.loadBankStatements();
        }
      },
      (error) => {
        this.backfillingStatementDates = false;
        console.error('Error backfilling statement dates:', error);
        this.snackBarService.showSimpleMsj('Failed to backfill statement dates: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private resolveBankAccountIdFromHolders(holders: any[], extractedCurrency?: string): number | null {
    const accountIds = new Set<number>();
    holders.forEach((holder: any) => {
      if (holder.bankAccountId) {
        accountIds.add(holder.bankAccountId);
      }
    });

    if (accountIds.size === 1) {
      return Array.from(accountIds)[0];
    }

    if (accountIds.size > 1 && extractedCurrency && extractedCurrency.trim()) {
      const normalizedCurrency = extractedCurrency.trim().toUpperCase();
      const currencyAccountIds = new Set<number>();
      holders.forEach((holder: any) => {
        if (holder.bankAccountId && holder.currency
            && holder.currency.trim().toUpperCase() === normalizedCurrency) {
          currencyAccountIds.add(holder.bankAccountId);
        }
      });
      if (currencyAccountIds.size === 1) {
        return Array.from(currencyAccountIds)[0];
      }
    }

    return null;
  }

  findAccountsAutomatically() {
    if (this.findingAccounts) {
      return;
    }

    // Filter statements that are eligible for auto-finding:
    // - Not confirmed
    // - Completed (extracted)
    // - Have statement start and end dates
    // - Have extracted account number
    const eligibleStatements = this.bankStatements.filter(item => {
      return !item.confirmed &&
             this.isCompleted(item) &&
             item.statementStartDate != null &&
             item.statementEndDate != null &&
             item.extractedAccountNumber &&
             item.extractedAccountNumber.trim().length > 0;
    });

    if (eligibleStatements.length === 0) {
      this.snackBarService.showSimpleMsj('No eligible bank statements found. Statements must be extracted, have statement dates, and not be confirmed yet.');
      return;
    }

    this.findingAccounts = true;
    this.snackBarService.showSimpleMsj(`Processing ${eligibleStatements.length} bank statement(s)...`);

    let processedCount = 0;
    let matchedCount = 0;
    let errorCount = 0;

    // Process statements sequentially to avoid overwhelming the server
    const processNext = (index: number) => {
      if (index >= eligibleStatements.length) {
        // All done
        this.findingAccounts = false;
        const message = `Processed ${processedCount} statement(s). ` +
                       `Matched and confirmed: ${matchedCount}. ` +
                       (errorCount > 0 ? `Errors: ${errorCount}. ` : '') +
                       'Refreshing list...';
        this.snackBarService.showSimpleMsj(message);
        this.loadBankStatements();
        return;
      }

      const item = eligibleStatements[index];
      processedCount++;

      // Try to find account holders for this statement's account number
      const payload = {
        accountNumber: item.extractedAccountNumber.trim()
      };

      this.requestService.postRequest(
        ConstantsService.BANK_STATEMENT_BATCH_FIND_ACCOUNT_HOLDERS,
        payload,
        new Headers()
      ).subscribe(
        (response: any) => {
          if (response && response.holders && Array.isArray(response.holders) && response.holders.length > 0) {
            const bankAccountId = this.resolveBankAccountIdFromHolders(response.holders, item.extractedCurrency);

            if (bankAccountId) {
              const matchingHolder = response.holders.find((holder: any) => holder.bankAccountId === bankAccountId)
                || response.holders[0];
              const clientId = matchingHolder.clientId;

              if (clientId && bankAccountId) {
                console.log(`Auto-matching statement ${item.id} to account ${bankAccountId}, client ${clientId}`);
                // Match the account
                const matchPayload = {
                  bankStatementId: item.id,
                  clientId: clientId,
                  bankAccountId: bankAccountId
                };

                this.requestService.postRequest(
                  ConstantsService.BANK_STATEMENT_BATCH_MATCH,
                  matchPayload,
                  new Headers()
                ).subscribe(
                  (matchResponse: any) => {
                    if (matchResponse && matchResponse.success) {
                      // After successful match, confirm and push
                      const confirmPayload = {
                        bankStatementId: item.id
                      };

                      this.requestService.postRequest(
                        ConstantsService.BANK_STATEMENT_BATCH_CONFIRM,
                        confirmPayload,
                        new Headers()
                      ).subscribe(
                        (confirmResponse: any) => {
                          if (confirmResponse && confirmResponse.success) {
                            matchedCount++;
                            // Process next statement
                            setTimeout(() => processNext(index + 1), 200);
                          } else {
                            errorCount++;
                            console.error('Failed to confirm statement:', item.id, confirmResponse);
                            // Process next statement anyway
                            setTimeout(() => processNext(index + 1), 200);
                          }
                        },
                        (confirmError: any) => {
                          errorCount++;
                          console.error('Error confirming statement:', item.id, confirmError);
                          // Process next statement anyway
                          setTimeout(() => processNext(index + 1), 200);
                        }
                      );
                    } else {
                      errorCount++;
                      console.error('Failed to match statement:', item.id, matchResponse);
                      // Process next statement anyway
                      setTimeout(() => processNext(index + 1), 200);
                    }
                  },
                  (matchError: any) => {
                    errorCount++;
                    console.error('Error matching statement:', item.id, matchError);
                    // Process next statement anyway
                    setTimeout(() => processNext(index + 1), 200);
                  }
                );
              } else {
                // Missing client or account ID
                errorCount++;
                console.error('Missing client or account ID for statement:', item.id);
                setTimeout(() => processNext(index + 1), 200);
              }
            } else {
              // Multiple accounts and no unambiguous currency match — skip
              console.log(`Skipping statement ${item.id}: no unambiguous account match`);
              setTimeout(() => processNext(index + 1), 200);
            }
          } else {
            // No holders found - skip this statement
            console.log(`Skipping statement ${item.id}: No account holders found`);
            // Process next statement
            setTimeout(() => processNext(index + 1), 200);
          }
        },
        (error: any) => {
          errorCount++;
          console.error('Error finding account holders for statement:', item.id, error);
          // Process next statement anyway
          setTimeout(() => processNext(index + 1), 200);
        }
      );
    };

    // Start processing from the first statement
    processNext(0);
  }

  loadBankStatements() {
    let url = `${ConstantsService.BANK_STATEMENT_BATCH_LIST}?showAll=${this.showAll}`;
    url += `&page=${this.currentPage}&pageSize=${this.pageSize}`;
    if (this.sortField) {
      url += `&sortField=${encodeURIComponent(this.sortField)}&sortOrder=${this.sortOrder === 1 ? 'ASC' : 'DESC'}`;
    }
    if (this.clientSearch && this.clientSearch.trim()) {
      url += `&clientSearch=${encodeURIComponent(this.clientSearch.trim())}`;
    }
    if (this.bankAccountSearch && this.bankAccountSearch.trim()) {
      url += `&bankAccountSearch=${encodeURIComponent(this.bankAccountSearch.trim())}`;
    }
    
    this.requestService.getRequest(url).subscribe(
      (response: any) => {
        // Handle paginated response
        const data = response.data || response; // Support both old and new response format
        this.totalRecords = response.totalRecords || data.length;
        
        // Add computed sortable properties for sorting (for client-side sorting of nested fields)
        this.bankStatements = (Array.isArray(data) ? data : []).map(item => {
          const result: BankStatementBatchItem = Object.assign({}, item);
          result.clientName = item.client ? item.client.clientName : '';
          result.bankAccountName = item.bankAccount ? item.bankAccount.accountName : '';
          return result;
        });
        this.applyFilter();
        this.startPollingForProcessing();
      },
      error => {
        this.snackBarService.showSimpleMsj('Error loading bank statements: ' + (error.message || 'Unknown error'));
      }
    );
  }

  applyFilter() {
    // With server-side pagination, filtering is done on the server
    // Just use the bankStatements directly
    this.filteredStatements = this.bankStatements;
  }

  onLazyLoad(event: any) {
    // Handle server-side pagination and sorting
    this.currentPage = (event.first / event.rows) + 1;
    this.pageSize = event.rows;
    
    // Handle sorting
    if (event.sortField) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder; // PrimeNG uses 1 for ascending, -1 for descending
    } else {
      this.sortField = null;
      this.sortOrder = 1;
    }
    
    // Reload with new pagination/sorting parameters
    this.loadBankStatements();
  }

  toggleShowAll() {
    // Don't toggle here - ngModel already handles it
    // Reset to first page when toggling filter
    this.currentPage = 1;
    // Reload with the new value
    this.loadBankStatements();
  }

  onClientSearchChange() {
    // Reset to first page when searching
    this.currentPage = 1;
    this.loadBankStatements();
  }

  onBankAccountSearchChange() {
    // Reset to first page when searching
    this.currentPage = 1;
    this.loadBankStatements();
  }

  clearClientSearch() {
    this.clientSearch = '';
    this.currentPage = 1;
    this.loadBankStatements();
  }

  clearBankAccountSearch() {
    this.bankAccountSearch = '';
    this.currentPage = 1;
    this.loadBankStatements();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    this.isDragging = false;

    const files = event.dataTransfer && event.dataTransfer.files;
    if (files && files.length > 0) {
      this.uploadFiles(Array.from(files) as File[]);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFiles(Array.from(input.files) as File[]);
    }
  }

  uploadFiles(files: File[]) {
    if (files.length === 0) {
      return;
    }

    this.isUploading = true;
    let uploadCount = 0;
    let errorCount = 0;
    const totalFiles = files.length;

    files.forEach((file, index) => {
      this.uploadFile(file, () => {
        uploadCount++;
        if (uploadCount + errorCount === totalFiles) {
          this.isUploading = false;
          if (uploadCount > 0) {
            this.snackBarService.showSimpleMsj(`Successfully uploaded ${uploadCount} file(s). Processing...`);
          }
          if (errorCount > 0) {
            this.snackBarService.showSimpleMsj(`Failed to upload ${errorCount} file(s).`);
          }
        }
      }, () => {
        errorCount++;
        if (uploadCount + errorCount === totalFiles) {
          this.isUploading = false;
        }
      });
    });
  }

  uploadFile(file: File, onSuccess?: () => void, onError?: () => void) {
    const formData = new FormData();
    formData.append('file', file);

    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_UPLOAD,
      formData,
      new Headers()
    ).subscribe(
      (response: any) => {
        if (response.duplicate) {
          // Don't show duplicate message for each file to avoid spam
          if (onSuccess) onSuccess();
        } else {
          // Add to list immediately
          const newItem: BankStatementBatchItem = {
            id: response.id,
            fileName: response.documentName || file.name,
            uniqueNameId: response.uniqueDocumentName,
            processingState: response.processingState || 'processing',
            confirmed: false
          };
          this.bankStatements.unshift(newItem);
          this.applyFilter();
          this.startPollingForItem(newItem.id);
          if (onSuccess) onSuccess();
        }
      },
      error => {
        if (onError) onError();
      }
    );
  }

  startPollingForProcessing() {
    // Only start polling if there are items actually processing
    const processingItems = this.bankStatements.filter(item => 
      item.processingState === 'processing' && !this.pollingIntervals.has(item.id)
    );
    
    if (processingItems.length === 0) {
      return; // No items to poll, exit early
    }
    
    processingItems.forEach(item => {
      this.startPollingForItem(item.id);
    });
  }

  startPollingForItem(id: number) {
    if (this.pollingIntervals.has(id)) {
      return; // Already polling
    }

    const interval = setInterval(() => {
      // For polling, we need to fetch the current page to find the item
      // Since we're using server-side pagination, we'll fetch the current page
      // and check if the item is there, or fetch all if needed
      let url = `${ConstantsService.BANK_STATEMENT_BATCH_LIST}?showAll=${this.showAll}`;
      url += `&page=${this.currentPage}&pageSize=${this.pageSize}`;
      if (this.sortField) {
        url += `&sortField=${encodeURIComponent(this.sortField)}&sortOrder=${this.sortOrder === 1 ? 'ASC' : 'DESC'}`;
      }
      
      this.requestService.getRequest(url).subscribe(
        (response: any) => {
          const data = response.data || response;
          const items = Array.isArray(data) ? data : [];
          const updatedItem = items.find((bs: BankStatementBatchItem) => bs.id === id);
          
          if (updatedItem) {
            const index = this.bankStatements.findIndex(bs => bs.id === id);
            if (index >= 0) {
              // Update the item with computed properties
              const result: BankStatementBatchItem = Object.assign({}, updatedItem);
              result.clientName = updatedItem.client ? updatedItem.client.clientName : '';
              result.bankAccountName = updatedItem.bankAccount ? updatedItem.bankAccount.accountName : '';
              this.bankStatements[index] = result;
              this.applyFilter();
              
              // Stop polling if processing is complete (not 'processing' anymore)
              // This includes: null (completed), 'completed', 'failed', or any other final state
              if (updatedItem.processingState !== 'processing') {
                clearInterval(interval);
                this.pollingIntervals.delete(id);
              }
            }
          }
        },
        error => {
          // Stop polling on error
          clearInterval(interval);
          this.pollingIntervals.delete(id);
        }
      );
    }, 3000); // Poll every 3 seconds

    this.pollingIntervals.set(id, interval);
  }

  isProcessing(item: BankStatementBatchItem): boolean {
    return item.processingState === 'processing';
  }

  isFailed(item: BankStatementBatchItem): boolean {
    return item.processingState === 'failed';
  }

  isCompleted(item: BankStatementBatchItem): boolean {
    return item.processingState === null || item.processingState === 'completed';
  }

  formatProcessingStartDate(item: BankStatementBatchItem): string {
    if (!item.processingStartDate) {
      return '-';
    }
    return formatDisplayDate(item.processingStartDate) || '-';
  }

  formatDate(timestamp: number | undefined): string {
    if (!timestamp) {
      return '-';
    }
    return formatDisplayDate(timestamp) || '-';
  }

  canConfirm(item: BankStatementBatchItem): boolean {
    // Check if analysis is completed, not already confirmed, and has a bank account matched
    // Note: We allow confirmation even with 0 transactions - the backend will handle it
    if (!this.isCompleted(item) || item.confirmed || item.bankAccount == null) {
      return false;
    }
    
    // Allow confirmation regardless of transaction count
    // The backend will mark as confirmed even if there are 0 transactions
    return true;
  }

  canPushToTransactionMonitoring(item: BankStatementBatchItem): boolean {
    if (!this.isCompleted(item) || this.isProcessing(item) || this.isFailed(item)) {
      return false;
    }
    if (!item || item.bankAccount == null) {
      return false;
    }
    if (!this.hasTransactions(item)) {
      return false;
    }
    // Only show/enable when we know it is not pushed yet
    return item.pushedToTransactionMonitoring === false;
  }

  hasTransactions(item: BankStatementBatchItem): boolean {
    // If transactionCount is provided, use it
    if (item.transactionCount !== undefined) {
      return item.transactionCount > 0;
    }
    
    // Otherwise, check jsonData for transactions
    if (!item.jsonData) {
      return false;
    }
    
    try {
      const jsonData = typeof item.jsonData === 'string' ? JSON.parse(item.jsonData) : item.jsonData;
      if (jsonData && jsonData.transactions && Array.isArray(jsonData.transactions)) {
        return jsonData.transactions.length > 0;
      }
    } catch (e) {
      console.warn('Error parsing jsonData to check transactions: ' + e.message);
    }
    
    return false;
  }

  findAccount(item: BankStatementBatchItem) {
    if (!item.extractedAccountNumber) {
      this.snackBarService.showSimpleMsj('Account number not available. Please wait for document analysis to complete.');
      return;
    }

    if (this.isProcessing(item)) {
      this.snackBarService.showSimpleMsj('Document is still being processed. Please wait.');
      return;
    }

    // Show the new guided find account dialog
    this.showFindAccountDialog(item);
  }

  showFindAccountDialog(item: BankStatementBatchItem) {
    const config = new MdDialogConfig();
    config.width = '700px';
    config.data = {
      bankStatementId: item.id,
      extractedAccountOwner: item.extractedAccountOwner || '',
      extractedAccountNumber: item.extractedAccountNumber || '',
      extractedCurrency: item.extractedCurrency || ''
    };

    const dialogRef = this.dialog.open(FindAccountDialogComponent, config);

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        if (result.matched) {
          const message = result.created 
            ? 'Account created and matched successfully!'
            : 'Account matched successfully!';
          this.snackBarService.showSimpleMsj(message);
          
          // Refresh the list to show the matched account
          this.loadBankStatements();
          
          // Auto-confirm and push if statement is not already confirmed
          // Only auto-confirm for statements that haven't been confirmed yet
          if (!item.confirmed) {
            // Use setTimeout to allow the match to complete on the backend before confirming
            setTimeout(() => {
              this.autoConfirmAfterMatch(item);
            }, 300);
          }
        } else if (result.created) {
          this.snackBarService.showSimpleMsj('Account created successfully!');
          this.loadBankStatements();
        }
      }
    });
  }

  showCreateAccountDialog(item: BankStatementBatchItem) {
    const config = new MdDialogConfig();
    config.width = '500px';
    config.data = {
      accountNumber: item.extractedAccountNumber,
      accountOwner: item.extractedAccountOwner,
      bankStatementId: item.id,
      clientId: item.client ? item.client.clientId : null,
      bankAccountId: item.bankAccount ? item.bankAccount.accountId : null
    };

    const dialogRef = this.dialog.open(CreateAccountDialogComponent, config);
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        // Account created, now match it to the bank statement
        if (result.bankAccountId) {
          this.matchBankStatement(item, result.clientId, result.bankAccountId);
        } else {
          this.snackBarService.showSimpleMsj('Account created but could not match to bank statement. Please match manually.');
          this.loadBankStatements();
        }
      }
    });
  }

  matchBankStatement(item: BankStatementBatchItem, clientId?: number, bankAccountId?: number) {
    const payload: any = {
      bankStatementId: item.id
    };
    if (clientId) {
      payload.clientId = clientId;
    }
    if (bankAccountId) {
      payload.bankAccountId = bankAccountId;
    }

    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_MATCH,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        if (response.success) {
          // After manual match, also auto-confirm (same as automatic flow)
          this.autoConfirmAfterMatch(item);
        }
      },
      error => {
        this.snackBarService.showSimpleMsj('Error matching bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }

  autoConfirmAfterMatch(item: BankStatementBatchItem) {
    // Auto-confirm after account match (same as automatic flow)
    const payload = {
      bankStatementId: item.id
    };

    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_CONFIRM,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        if (response.success) {
          const transactionsCreated = response.created || 0;
          if (transactionsCreated > 0) {
            this.snackBarService.showSimpleMsj(`Bank statement matched and confirmed! ${transactionsCreated} transaction(s) pushed to monitoring.`);
          } else {
            this.snackBarService.showSimpleMsj('Bank statement matched and confirmed! (No transactions to push)');
          }
          this.loadBankStatements();
        } else {
          this.snackBarService.showSimpleMsj('Bank statement matched but confirmation failed: ' + (response.message || 'Unknown error'));
          this.loadBankStatements();
        }
      },
      error => {
        this.snackBarService.showSimpleMsj('Bank statement matched but error confirming: ' + (error.message || 'Unknown error'));
        this.loadBankStatements();
      }
    );
  }

  confirmBankStatement(item: BankStatementBatchItem) {
    if (!this.canConfirm(item)) {
      this.snackBarService.showSimpleMsj('Cannot confirm: Bank account must be matched first');
      return;
    }

    const payload = {
      bankStatementId: item.id
    };

    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_CONFIRM,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        if (response.success) {
          this.snackBarService.showSimpleMsj(response.message || 'Bank statement confirmed and transactions pushed');
          this.loadBankStatements();
        } else {
          this.snackBarService.showSimpleMsj(response.message || 'Bank statement confirmed but failed to push transactions');
          this.loadBankStatements();
        }
      },
      error => {
        this.snackBarService.showSimpleMsj('Error confirming bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }

  pushToTransactionMonitoring(item: BankStatementBatchItem) {
    if (!item || !item.id) {
      this.snackBarService.showSimpleMsj('Missing bank statement ID');
      return;
    }
    if (!this.hasTransactions(item)) {
      this.snackBarService.showSimpleMsj('No transactions found in bank statement');
      return;
    }
    this.financeService.pushToTransactionMonitoring(
      item.id,
      item.client ? item.client.clientId : item.matchedClientId
    ).subscribe(
      (response: any) => {
        const result = (response && response.arrResult) ? response.arrResult : response;
        if (result && (result.created !== undefined || result.skipped !== undefined || result.reassigned !== undefined)) {
          this.snackBarService.showSimpleMsj(
            `Pushed ${result.created || 0} transactions. ${result.reassigned || 0} reassigned. ${result.skipped || 0} already pushed.`
          );
        } else {
          this.snackBarService.showSimpleMsj('Successfully pushed transactions to Transaction Monitoring');
        }
        this.loadBankStatements();
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Error pushing transactions: ' + (error.message || 'Unknown error'));
      }
    );
  }

  investigateDuplicates(item: BankStatementBatchItem) {
    if (!item || !item.id) {
      this.snackBarService.showSimpleMsj('Missing bank statement ID');
      return;
    }

    this.financeService.checkDuplicates(item.id).subscribe(
      (response: any) => {
        const payload = response && response.arrResult ? response.arrResult : response;
        const duplicates = payload && payload.duplicates ? payload.duplicates : [];
        const total = payload && payload.total !== undefined ? payload.total : 0;
        const duplicateCount = payload && payload.duplicateCount !== undefined ? payload.duplicateCount : (Array.isArray(duplicates) ? duplicates.length : 0);

        const config = new MdDialogConfig();
        config.width = '95%';
        config.data = {
          bankStatement: item,
          duplicates: duplicates,
          total: total,
          duplicateCount: duplicateCount
        };

        const dialogRef = this.dialog.open(DuplicatesInvestigationDialogComponent, config);
        dialogRef.afterClosed().subscribe((result: any) => {
          if (result && result.refresh) {
            this.loadBankStatements();
          }
        });
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Failed to check duplicates: ' + (error.message || 'Unknown error'));
      }
    );
  }

  previewBankStatement(item: BankStatementBatchItem) {
    // Open preview dialog similar to transaction monitoring
    const config = new MdDialogConfig();
    config.width = '80%';
    config.height = '80%';
    config.data = {
      bankStatementId: item.id,
      uniqueNameId: item.uniqueNameId,
      bankAccountId: item.bankAccount ? item.bankAccount.accountId : null
    };
    
    this.dialog.open(BankStatementPreviewComponent, config);
  }

  viewTransactions(item: BankStatementBatchItem) {
    // Fetch bank statement details to get transaction data
    const url = `/bank-statement/${item.id}`;
    this.requestService.getRequest(url).subscribe(
      (bankStatementDetails: any) => {
        // Parse jsonData if available
        let jsonData = null;
        if (bankStatementDetails.jsonData) {
          try {
            jsonData = typeof bankStatementDetails.jsonData === 'string' 
              ? JSON.parse(bankStatementDetails.jsonData) 
              : bankStatementDetails.jsonData;
          } catch (e) {
            console.error('Error parsing jsonData: ' + e.message);
            this.snackBarService.showSimpleMsj('Error parsing transaction data');
            return;
          }
        }
        
        // Prepare data structure for ViewTransactionComponent
        // The component expects: { jsonData: { transactions: [...] } }
        const transactionData = {
          jsonData: jsonData || { transactions: [] }
        };
        
        // Open the transaction view dialog
        const config = new MdDialogConfig();
        config.width = '80%';
        config.height = '80%';
        const dialogRef = this.dialog.open(ViewTransactionComponent, config);
        dialogRef.componentInstance.jsonData = transactionData;
      },
      (error: any) => {
        console.error('Error loading bank statement details: ' + (error.message || 'Unknown error'));
        this.snackBarService.showSimpleMsj('Error loading transactions: ' + (error.message || 'Unknown error'));
      }
    );
  }

  reRunAnalyzeDocument(item: BankStatementBatchItem) {
    if (this.isProcessing(item)) {
      this.snackBarService.showSimpleMsj('Document is already being processed');
      return;
    }

    // Check if bank statement is confirmed - if so, show warning
    if (item.confirmed) {
      this.showReanalyzeWarningDialog(item);
      return;
    }

    // Proceed with re-analyze for non-confirmed statements
    this.proceedWithReanalyze(item);
  }

  showReanalyzeWarningDialog(item: BankStatementBatchItem) {
    const config = new MdDialogConfig();
    config.width = '500px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
    
    dialogRef.componentInstance.dialogMessage = 
      'WARNING: This bank statement is already confirmed and has transactions in transaction monitoring. ' +
      'Re-analyzing will delete all existing monitored transactions from this bank statement and re-run the rules after re-extraction. ' +
      'Are you sure you want to continue?';
    dialogRef.componentInstance.confirmationButtonLabel = 'Continue';
    
    dialogRef.componentInstance.onConfirmed = (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.proceedWithReanalyze(item);
      }
    };
  }

  proceedWithReanalyze(item: BankStatementBatchItem) {
    // Get bank statement details including JSON data
    const url = `/bank-statement/${item.id}`;
    this.requestService.getRequest(url).subscribe(
      (bankStatementDetails: any) => {
        // Get existing transactions from current bank statement
        let existingTransactions: any[] = [];
        if (bankStatementDetails.jsonData) {
          try {
            const existingJsonData = JSON.parse(bankStatementDetails.jsonData);
            existingTransactions = existingJsonData.transactions || [];
          } catch (e) {
            console.warn('Error parsing existing JSON data: ' + e.message);
          }
        }

        // Store existing transactions for later comparison
        const originalTransactions = existingTransactions;

        // Call analyze endpoint - backend will construct path from bankStatementId
        // Pass null documentPath to let backend construct it based on whether bank account is set
        this.financeService.getDocumentAnalysis(
          item.uniqueNameId,
          item.bankAccount ? item.bankAccount.accountId : 0, // Will be ignored if bankStatementId is provided
          bankStatementDetails.tenantId,
          item.id,
          null // Let backend construct the path
        ).subscribe(
          (responseRequest: any) => {
            // Check if response contains status information
            if (responseRequest && responseRequest.status) {
              if (responseRequest.status === 'processing') {
                // Processing started asynchronously
                this.snackBarService.showSimpleMsj('Document re-analysis started. Processing in background...');
                // Update item state and start polling
                item.processingState = 'processing';
                this.startPollingForItem(item.id);
                
                // Set up a one-time check after processing completes to show comparison dialog
                this.waitForReanalysisCompletion(item.id, originalTransactions);
                return;
              } else if (responseRequest.status === 'failed') {
                // Analysis failed to start
                item.processingState = 'failed';
                this.snackBarService.showSimpleMsj('Failed to start document re-analysis: ' + (responseRequest.message || 'Unknown error'));
                return;
              }
            }

            // Legacy synchronous processing or completed analysis
            if (responseRequest && responseRequest.transactions) {
              // Show comparison dialog immediately
              this.showReanalyzeComparisonDialog(item.id, originalTransactions, responseRequest);
            } else {
              this.snackBarService.showSimpleMsj('Re-analysis completed but no transactions found');
              this.loadBankStatements();
            }
          },
          (error: any) => {
            this.snackBarService.showSimpleMsj('Error re-analyzing document: ' + (error.message || 'Unknown error'));
            console.error('Error in re-analyze: ' + error.message);
          }
        );
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Error loading bank statement details: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private waitForReanalysisCompletion(bankStatementId: number, originalTransactions: any[]) {
    // Poll for completion - check every 2 seconds, max 30 times (1 minute)
    let attempts = 0;
    const maxAttempts = 30;
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      // Get updated bank statement
      const url = `/bank-statement/${bankStatementId}`;
      this.requestService.getRequest(url).subscribe(
        (bankStatementDetails: any) => {
          // Check if processing is complete (completed or failed)
          if (bankStatementDetails.processingState !== 'processing' && 
              bankStatementDetails.processingState !== null && 
              bankStatementDetails.processingState !== undefined) {
            clearInterval(checkInterval);
            this.reanalysisIntervals.delete(checkInterval);
            
            if (bankStatementDetails.processingState === 'failed') {
              this.snackBarService.showSimpleMsj('Document re-analysis failed');
              return;
            }
            
            // Processing completed - get new transactions
            if (bankStatementDetails.jsonData) {
              try {
                const newJsonData = JSON.parse(bankStatementDetails.jsonData);
                const newTransactions = newJsonData.transactions || [];
                
                // Re-fetch original transactions right before comparison to ensure we have the latest
                // This handles cases where the database might have been updated
                this.fetchOriginalTransactionsForComparison(bankStatementId, newJsonData);
              } catch (e) {
                console.error('Error parsing new JSON data: ' + e.message);
                this.snackBarService.showSimpleMsj('Re-analysis completed but error parsing results');
              }
            }
          } else if (attempts >= maxAttempts) {
            // Timeout
            clearInterval(checkInterval);
            this.reanalysisIntervals.delete(checkInterval);
            this.snackBarService.showSimpleMsj('Re-analysis is taking longer than expected. Please refresh the page to see results.');
          }
        },
        (error: any) => {
          clearInterval(checkInterval);
          this.reanalysisIntervals.delete(checkInterval);
          console.error('Error checking re-analysis status: ' + error.message);
        }
      );
    }, 2000); // Check every 2 seconds
    
    // Track this interval for cleanup
    this.reanalysisIntervals.add(checkInterval);
  }

  private fetchOriginalTransactionsForComparison(bankStatementId: number, newJsonData: any) {
    // Fetch the current bank statement to get the original transactions
    // We need to get the transactions that were there BEFORE the async analysis updated them
    // So we'll look for transactions that have transaction_id (already pushed) or use a backup
    const url = `/bank-statement/${bankStatementId}`;
    this.requestService.getRequest(url).subscribe(
      (bankStatementDetails: any) => {
        let originalTransactions: any[] = [];
        
        // Try to get original transactions from the current JSON data
        // If the new analysis just completed, the database might already have the new data
        // So we'll use the newJsonData transactions as the "original" if we can't find old ones
        if (bankStatementDetails.jsonData) {
          try {
            const currentJsonData = JSON.parse(bankStatementDetails.jsonData);
            const currentTransactions = currentJsonData.transactions || [];
            
            // If current transactions match new transactions, they might be the same
            // In this case, we should use newJsonData.transactions as the original
            if (currentTransactions.length === 0 && newJsonData.transactions && newJsonData.transactions.length > 0) {
              // Database has empty transactions but new analysis has data
              // This means the database was cleared - use new transactions as original
              originalTransactions = newJsonData.transactions;
            } else {
              // Use current transactions as original
              originalTransactions = currentTransactions;
            }
          } catch (e) {
            console.warn('Error parsing current JSON data, using new transactions as original: ' + e.message);
            originalTransactions = newJsonData.transactions || [];
          }
        } else {
          // No JSON data, use new transactions as original
          originalTransactions = newJsonData.transactions || [];
        }
        
        // Show comparison dialog with fetched original transactions
        this.showReanalyzeComparisonDialog(bankStatementId, originalTransactions, newJsonData);
      },
      (error: any) => {
        console.error('Error fetching original transactions: ' + error.message);
        // Fallback: use new transactions as original if we can't fetch
        this.showReanalyzeComparisonDialog(bankStatementId, newJsonData.transactions || [], newJsonData);
      }
    );
  }

  private showReanalyzeComparisonDialog(bankStatementId: number, originalTransactions: any[], newJsonData: any) {
    const newTransactions = newJsonData.transactions || [];
    
    // Check if old and new transactions are the same
    if (this.areTransactionsIdentical(originalTransactions, newTransactions)) {
      // Transactions are identical, automatically apply merge (keep all) without showing dialog
      // If originalTransactions is empty but newTransactions has data, use newTransactions
      // This handles the case where the database had empty transactions from a previous failed merge
      const transactionsToKeep = (originalTransactions && originalTransactions.length > 0) 
        ? originalTransactions 
        : newTransactions;
      
      const mergeResult = {
        toRemove: [],
        toAdd: [],
        toKeep: transactionsToKeep
      };
      
      console.log('Auto-merge: originalTransactions count:', originalTransactions ? originalTransactions.length : 0);
      console.log('Auto-merge: newTransactions count:', newTransactions ? newTransactions.length : 0);
      console.log('Auto-merge: transactionsToKeep count:', transactionsToKeep ? transactionsToKeep.length : 0);
      
      this.applyReanalyzeMerge(bankStatementId, mergeResult, newJsonData);
      this.snackBarService.showSimpleMsj('Re-analysis completed. No changes detected - transactions are identical.');
      return;
    }
    
    // Transactions differ, show comparison dialog
    const dialogConfig = new MdDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.data = {
      originalTransactions: originalTransactions,
      newTransactions: newTransactions,
      bankStatementId: bankStatementId
    };

    const dialogRef = this.dialog.open(ReanalyzeComparisonComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        // Apply the merge
        this.applyReanalyzeMerge(bankStatementId, result, newJsonData);
      }
    });
  }

  private areTransactionsIdentical(originalTransactions: any[], newTransactions: any[]): boolean {
    // Use the same key function as ReanalyzeComparisonComponent
    const getTransactionKey = (tx: any): string => {
      const date = tx.tx_date || '';
      const amount = tx.tx_amount || '';
      const description = (tx.tx_description || '').substring(0, 50);
      const counterparty = tx.tx_counter_party || '';
      return `${date}::${amount}::${description}::${counterparty}`;
    };

    // If counts differ, they're not identical
    if (originalTransactions.length !== newTransactions.length) {
      return false;
    }

    // Create sets of transaction keys
    const originalKeys = new Set(originalTransactions.map(tx => getTransactionKey(tx)));
    const newKeys = new Set(newTransactions.map(tx => getTransactionKey(tx)));

    // Check if all keys match
    if (originalKeys.size !== newKeys.size) {
      return false;
    }

    // Check if all keys in original exist in new (convert Set to array for iteration)
    const originalKeysArray = Array.from(originalKeys);
    for (let i = 0; i < originalKeysArray.length; i++) {
      if (!newKeys.has(originalKeysArray[i])) {
        return false;
      }
    }

    return true;
  }

  private applyReanalyzeMerge(bankStatementId: number, mergeResult: any, newJsonData: any) {
    // Call backend to apply the merge
    this.financeService.applyReanalyzeMerge(bankStatementId, mergeResult, newJsonData).subscribe(
      (response: any) => {
        this.snackBarService.showSimpleMsj('Bank statement reanalyzed and merged successfully');
        
        // After merge, automatically try to find account if not already matched (only for batch statements)
        this.autoFindAccountAfterReanalysis(bankStatementId);
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Error applying merge: ' + (error.message || 'Unknown error'));
      }
    );
  }

  deleteBankStatement(item: BankStatementBatchItem) {
    // Show warning if bank statement is confirmed
    if (item.confirmed) {
      this.showDeleteWarningDialog(item);
    } else {
      this.showDeleteConfirmDialog(item);
    }
  }

  showDeleteWarningDialog(item: BankStatementBatchItem) {
    const config = new MdDialogConfig();
    config.width = '500px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
    
    dialogRef.componentInstance.dialogMessage = 
      'WARNING: This bank statement is confirmed and has transactions in transaction monitoring. ' +
      'Deleting this bank statement will also delete all associated transaction records that are already monitored. ' +
      'Are you sure you want to delete this bank statement?';
    dialogRef.componentInstance.confirmationButtonLabel = 'Delete';
    
    dialogRef.componentInstance.onConfirmed = (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.proceedWithDelete(item);
      }
    };
  }

  showDeleteConfirmDialog(item: BankStatementBatchItem) {
    const config = new MdDialogConfig();
    config.width = '500px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
    
    dialogRef.componentInstance.dialogMessage = 'Are you sure you want to delete this bank statement?';
    dialogRef.componentInstance.confirmationButtonLabel = 'Delete';
    
    dialogRef.componentInstance.onConfirmed = (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.proceedWithDelete(item);
      }
    };
  }

  proceedWithDelete(item: BankStatementBatchItem) {
    this.financeService.deleteBankStatement(item.id.toString()).subscribe(
      () => {
        this.snackBarService.showSimpleMsj('Bank statement deleted successfully.');
        this.loadBankStatements();
      },
      (error: any) => {
        console.error('Error deleting bank statement: ', error);
        this.snackBarService.showSimpleMsj('Cannot delete bank statement: ' + (error.message || 'Unknown error'));
      }
    );
  }


  private autoFindAccountAfterReanalysis(bankStatementId: number) {
    // Get updated bank statement to check if it has a bank account
    const url = `/bank-statement/${bankStatementId}`;
    this.requestService.getRequest(url).subscribe(
      (bankStatementDetails: any) => {
        // Only try to find account if no bank account is mapped yet (batch statement scenario)
        if (!bankStatementDetails.bankAccount && bankStatementDetails.extractedAccountNumber) {
          // Try to find matching account
          const payload = {
            bankStatementId: bankStatementId
          };

          this.requestService.postRequest(
            ConstantsService.BANK_STATEMENT_BATCH_FIND_ACCOUNT,
            payload,
            new Headers()
          ).subscribe(
            (response: any) => {
              if (response.found) {
                // Account was found and matched automatically
                this.snackBarService.showSimpleMsj('Account automatically matched: ' + 
                  (bankStatementDetails.extractedAccountOwner || bankStatementDetails.extractedAccountNumber));
                this.loadBankStatements();
              } else {
                // No account found, just refresh the list
                this.loadBankStatements();
              }
            },
            (error: any) => {
              // Error finding account, but still refresh the list
              console.warn('Error auto-finding account after re-analysis: ' + (error.message || 'Unknown error'));
              this.loadBankStatements();
            }
          );
        } else {
          // Already has bank account or no account number, just refresh
          this.loadBankStatements();
        }
      },
      (error: any) => {
        // Error loading bank statement, just refresh the list
        console.warn('Error loading bank statement after re-analysis: ' + (error.message || 'Unknown error'));
        this.loadBankStatements();
      }
    );
  }

  onTimelineToggle() {
    if (this.showTimelineView) {
      this.loadTimelineData();
    }
  }

  loadTimelineData() {
    this.loadingTimeline = true;
    const url = ConstantsService.BANK_STATEMENT_BATCH_TIMELINE;
    
    this.requestService.getRequest(url).subscribe(
      (response: any) => {
        this.loadingTimeline = false;
        if (response && Array.isArray(response)) {
          this.timelineData = this.processTimelineData(response);
        } else if (response && response.arrResult && Array.isArray(response.arrResult)) {
          this.timelineData = this.processTimelineData(response.arrResult);
        } else {
          this.timelineData = [];
        }
      },
      (error: any) => {
        this.loadingTimeline = false;
        console.error('Error loading timeline data:', error);
        this.snackBarService.showSimpleMsj('Failed to load timeline data: ' + (error.message || 'Unknown error'));
        this.timelineData = [];
      }
    );
  }

  processTimelineData(data: any[]): any[] {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Determine start date: beginning of current year, or previous year if less than 3 months visible
    let startDate: Date;
    if (currentMonth < 3) {
      // Less than 3 months into the year, start from previous year
      startDate = new Date(currentYear - 1, 0, 1);
    } else {
      startDate = new Date(currentYear, 0, 1);
    }
    
    const endDate = new Date(currentYear, currentMonth + 1, 0); // End of current month
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return data.map(client => {
      const bankAccounts = (client.bankAccounts || []).map((account: any) => {
        const statements = account.statements || [];
        
        // Sort statements by start date
        statements.sort((a: any, b: any) => {
          const dateA = a.statementStartDate ? new Date(a.statementStartDate) : null;
          const dateB = b.statementStartDate ? new Date(b.statementStartDate) : null;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime();
        });
        
        // Calculate timeline months
        const timelineMonths = this.calculateTimelineMonths(statements, startDate, endDate, totalDays);
        const monthLabels = this.generateMonthLabels(startDate, endDate);
        
        return Object.assign({}, account, {
          timelineMonths: timelineMonths,
          monthLabels: monthLabels
        });
      });
      
      return Object.assign({}, client, {
        bankAccounts: bankAccounts
      });
    });
  }

  calculateTimelineMonths(statements: any[], startDate: Date, endDate: Date, totalDays: number): any[] {
    const months: any[] = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const monthStartDay = this.startOfDay(monthStart);
      const monthEndDay = this.endOfDay(monthEnd);
      
      // Find statements that overlap with this month
      const overlappingStatements = statements.filter((stmt: any) => {
        if (!stmt.statementStartDate || !stmt.statementEndDate) return false;
        const stmtStart = this.startOfDay(new Date(stmt.statementStartDate));
        const stmtEnd = this.endOfDay(new Date(stmt.statementEndDate));
        
        // Check if statement overlaps with month
        return (stmtStart.getTime() <= monthEndDay.getTime() && stmtEnd.getTime() >= monthStartDay.getTime());
      });
      
      // Determine if month is fully covered, has gaps, or has no data
      let color = '#e0e0e0'; // No data (gray)
      let tooltip = `${this.formatMonthYear(monthStart)}: No data`;
      let hasGap = false;
      const hasData = overlappingStatements.length > 0;
      
      if (overlappingStatements.length > 0) {
        // Sort overlapping statements by start date (day precision)
        overlappingStatements.sort((a: any, b: any) => {
          return this.startOfDay(new Date(a.statementStartDate)).getTime() - this.startOfDay(new Date(b.statementStartDate)).getTime();
        });

        // Walk day-by-day coverage to detect gaps (treating statement end date as inclusive)
        let cursor = monthStartDay.getTime(); // expected next covered day (00:00)
        const monthEndTime = monthEndDay.getTime(); // inclusive (23:59:59.999)

        for (const stmt of overlappingStatements) {
          const stmtStart = this.startOfDay(new Date(stmt.statementStartDate)).getTime();
          const stmtEnd = this.endOfDay(new Date(stmt.statementEndDate)).getTime();

          const overlapStart = Math.max(stmtStart, monthStartDay.getTime());
          const overlapEnd = Math.min(stmtEnd, monthEndTime);
          if (overlapStart > overlapEnd) {
            continue;
          }

          if (overlapStart > cursor) {
            hasGap = true;
            break;
          }

          // Move cursor to the start of the day after this covered period
          cursor = this.startOfDay(this.addDays(new Date(overlapEnd), 1)).getTime();
          if (cursor > monthEndTime) {
            // fully covered through end of month
            break;
          }
        }

        // If we never reached month end, there is a trailing gap
        if (!hasGap) {
          if (cursor <= monthEndTime) {
            hasGap = true;
          }
        }
        
        if (hasGap) {
          color = '#f44336'; // Gap (red)
          tooltip = `${this.formatMonthYear(monthStart)}: Has gaps in coverage`;
        } else {
          color = '#4caf50'; // Covered (green)
          tooltip = `${this.formatMonthYear(monthStart)}: Fully covered`;
        }
      }
      
      // Calculate position and width
      const daysFromStart = Math.floor((monthStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysInMonth = monthEnd.getDate();
      const leftPercent = (daysFromStart / totalDays) * 100;
      const widthPercent = (daysInMonth / totalDays) * 100;
      
      months.push({
        color,
        tooltip,
        leftPercent,
        widthPercent,
        hasGap,
        isClickable: hasData,
        monthStartTime: monthStartDay.getTime(),
        monthEndTime: monthEndDay.getTime()
      });
      
      // Move to next month
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  }

  onTimelineMonthClick(client: any, account: any, month: any, event: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!month || !month.isClickable) {
      return;
    }

    const statements = (account && account.statements) ? account.statements : [];
    const periods = this.buildTimelineMonthPeriods(
      statements,
      month.monthStartTime,
      month.monthEndTime
    );

    const config = new MdDialogConfig();
    config.width = '980px';
    config.data = {
      clientName: client ? client.clientName : '',
      accountName: account ? (account.accountName || account.accountNumber) : '',
      accountNumber: account ? account.accountNumber : '',
      bankName: account ? account.bankName : '',
      monthLabel: this.formatMonthYear(new Date(month.monthStartTime)),
      periods: periods
    };

    const dialogRef = this.dialog.open(TimelineMonthDetailsDialogComponent, config);
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.refreshTimeline) {
        this.loadTimelineData();
      }
    });
  }

  private buildTimelineMonthPeriods(statements: any[], monthStartTime: number, monthEndTime: number): TimelineMonthPeriodRow[] {
    const periods: TimelineMonthPeriodRow[] = [];

    if (!monthStartTime || !monthEndTime) {
      return periods;
    }

    const monthStart = this.startOfDay(new Date(monthStartTime));
    const monthEnd = this.endOfDay(new Date(monthEndTime));
    const monthStartMs = monthStart.getTime();
    const monthEndMs = monthEnd.getTime();

    const overlapping = (Array.isArray(statements) ? statements : [])
      .filter((stmt: any) => stmt && stmt.statementStartDate && stmt.statementEndDate)
      .map((stmt: any) => {
        const stmtStart = this.startOfDay(new Date(stmt.statementStartDate)).getTime();
        const stmtEnd = this.endOfDay(new Date(stmt.statementEndDate)).getTime();
        const start = Math.max(stmtStart, monthStartMs);
        const end = Math.min(stmtEnd, monthEndMs);
        return Object.assign({}, stmt, { _overlapStart: start, _overlapEnd: end });
      })
      .filter((stmt: any) => stmt._overlapStart <= stmt._overlapEnd)
      .sort((a: any, b: any) => a._overlapStart - b._overlapStart);

    let cursor = monthStartMs; // day start
    let lastStatement: { statementId: number; label: string } = null;

    overlapping.forEach((stmt: any, idx: number) => {
      const s = this.startOfDay(new Date(stmt._overlapStart)).getTime();
      const e = this.endOfDay(new Date(stmt._overlapEnd)).getTime();

      if (s > cursor) {
        const gapEnd = this.endOfDay(this.addDays(new Date(s), -1)).getTime();
        if (cursor <= gapEnd) {
          const nextStmtLabel = stmt.fileName || stmt.documentName || stmt.uniqueNameId || (stmt.id ? `Statement #${stmt.id}` : 'Bank statement');
          const nextStmtId = stmt.id != null ? Number(stmt.id) : null;
          const candidates: Array<{ target: 'before' | 'after'; statementId: number; label: string }> = [];
          if (lastStatement && lastStatement.statementId) {
            candidates.push({ target: 'before', statementId: lastStatement.statementId, label: lastStatement.label });
          }
          if (nextStmtId) {
            candidates.push({ target: 'after', statementId: nextStmtId, label: nextStmtLabel });
          }
          periods.push({
            type: 'gap',
            start: cursor,
            end: gapEnd,
            include: { isEditing: false },
            includeCandidates: candidates
          });
        }
      }

      const label = stmt.fileName || stmt.documentName || stmt.uniqueNameId || (stmt.id ? `Statement #${stmt.id}` : 'Bank statement');
      const statementId = stmt.id != null ? Number(stmt.id) : null;
      periods.push({ type: 'statement', start: s, end: e, label: label, statementId: statementId });
      if (statementId) {
        lastStatement = { statementId: statementId, label: label };
      }

      cursor = this.startOfDay(this.addDays(new Date(e), 1)).getTime();
    });

    if (cursor <= monthEndMs) {
      const candidates: Array<{ target: 'before' | 'after'; statementId: number; label: string }> = [];
      if (lastStatement && lastStatement.statementId) {
        candidates.push({ target: 'before', statementId: lastStatement.statementId, label: lastStatement.label });
      }
      periods.push({
        type: 'gap',
        start: cursor,
        end: monthEndMs,
        include: { isEditing: false },
        includeCandidates: candidates
      });
    }

    return periods;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 0, 0, 0, 0);
  }

  generateMonthLabels(startDate: Date, endDate: Date): any[] {
    const labels: any[] = [];
    const current = new Date(startDate);
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    while (current <= endDate) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const daysFromStart = Math.floor((monthStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const leftPercent = (daysFromStart / totalDays) * 100;
      
      labels.push({
        label: this.formatMonthAxisLabel(monthStart, startDate),
        fullLabel: this.formatMonthYear(monthStart),
        leftPercent
      });
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return labels;
  }

  formatMonthYear(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  /**
   * Timeline axis label: keep it compact to reduce overlap.
   * Show year on the first month and on every January.
   */
  formatMonthAxisLabel(date: Date, startDate: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const isFirst = date.getFullYear() === startDate.getFullYear() && date.getMonth() === startDate.getMonth();
    const isJanuary = date.getMonth() === 0;
    const shortYear = String(date.getFullYear()).slice(-2);
    return (isFirst || isJanuary) ? `${months[date.getMonth()]} ${shortYear}` : months[date.getMonth()];
  }
}



// WEBPACK FOOTER //
// ./src/app/finance/bank-statements-batch/bank-statements-batch.component.ts